import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, RotateCcw } from 'lucide-react';
import { Button, IconButton, Spinner } from '@ntconsult/aluma-react';
import { useDownload } from '../hooks/useDownload';
import { useVideoPlayer } from '../hooks/useVideoPlayer';
import { VideoControls } from './VideoControls';
import { formatDuration } from '@common/utils/format';
import { cn } from '@common/utils/cn';
import type { File } from '../types';

type Player = ReturnType<typeof useVideoPlayer>;

interface EmbeddedVideoPlayerProps {
  file: File;
  player: Player;
}

export function EmbeddedVideoPlayer({ file, player }: EmbeddedVideoPlayerProps) {
  const { t } = useTranslation('video');
  const { videoRef, containerRef, streamProxyUrl, state, actions } = player;
  const { mutate: download } = useDownload();

  const pipSupported =
    typeof document !== 'undefined' && (document as Document).pictureInPictureEnabled === true;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      if (target?.isContentEditable) return;

      switch (e.key) {
        case ' ':
        case 'k':
        case 'K':
          e.preventDefault();
          actions.togglePlay();
          break;
        case 'ArrowLeft':
          actions.skip(-10);
          break;
        case 'ArrowRight':
          actions.skip(10);
          break;
        case 'f':
        case 'F':
          actions.toggleFullscreen();
          break;
        case 'm':
        case 'M':
          actions.toggleMute();
          break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [actions]);

  const aspectStyle = state.aspectRatio
    ? { aspectRatio: state.aspectRatio }
    : { aspectRatio: 16 / 9 };

  const isPortrait = state.aspectRatio !== null && state.aspectRatio < 1;

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative w-full bg-black rounded-lg overflow-hidden group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2',
        isPortrait && 'max-h-[80vh] mx-auto',
      )}
      style={isPortrait ? { maxWidth: `calc(80vh * ${state.aspectRatio})` } : undefined}
      onMouseMove={actions.handleMouseMove}
      onMouseLeave={actions.handleMouseLeave}
    >
      <div className="w-full" style={aspectStyle}>
        <video
          ref={videoRef}
          src={streamProxyUrl}
          crossOrigin="use-credentials"
          className="w-full h-full object-contain bg-black"
          preload="metadata"
          playsInline
          onClick={state.hasStarted ? actions.togglePlay : undefined}
        />
      </div>

      {!state.hasStarted && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
          <div className="flex flex-col items-center gap-3">
            <IconButton
              variant="primary"
              size="lg"
              label={t('player.play')}
              icon={<Play className="w-7 h-7 ml-1" aria-hidden="true" />}
              onClick={actions.play}
              className="!w-16 !h-16 rounded-full shadow-2xl cursor-pointer"
            />
            {state.savedPosition != null && (
              <div className="dark flex items-center gap-2 px-3 py-2 rounded-lg bg-surface/90 backdrop-blur shadow-lg">
                <span className="text-xs text-foreground-muted">
                  {t('player.resumeFromHint', {
                    time: formatDuration(Math.floor(state.savedPosition)),
                  })}
                </span>
                <Button size="xs" variant="primary" onClick={actions.resumeFromSaved}>
                  {t('player.resumeFromAction')}
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={actions.startOver}
                  leftIcon={<RotateCcw className="w-3 h-3" aria-hidden="true" />}
                >
                  {t('player.startOver')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {state.hasStarted && state.isBuffering && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Spinner size="lg" />
        </div>
      )}

      {state.hasStarted && (
        <VideoControls
          t={t}
          showControls={state.showControls || !state.isPlaying}
          isPlaying={state.isPlaying}
          isMuted={state.isMuted}
          isFullscreen={state.isFullscreen}
          isPip={state.isPip}
          currentTime={state.currentTime}
          duration={state.duration}
          volume={state.volume}
          playbackRate={state.playbackRate}
          showTimeAsRemaining={state.showTimeAsRemaining}
          pipSupported={pipSupported}
          onTogglePlay={actions.togglePlay}
          onToggleMute={actions.toggleMute}
          onToggleFullscreen={actions.toggleFullscreen}
          onTogglePip={actions.togglePip}
          onSeek={actions.seekTo}
          onVolumeChange={actions.setVolumeAt}
          onSkip={actions.skip}
          onDownload={() => download(file.id)}
          onChangeRate={actions.setRate}
          onToggleTimeDisplay={actions.toggleTimeDisplay}
        />
      )}
    </div>
  );
}
